// The definition as the Designer edits it (mirrors services/api/src/ja/model.ts), plus Waypoint's reading of an action.

export type ValueSource =
  | { kind: "literal"; value: string }
  | { kind: "prompt"; code: string; format?: "yyyymmdd" }
  | { kind: "builtin"; name: "reportName" | "workTitle"; arg?: string }
  | { kind: "report"; template: string }
  /** The current row of a step that runs for each row of an array: its value, or an attribute. */
  | { kind: "array"; attr?: string }
  /** Every row of an array, sent as fields <name>1..<name>N. */
  | { kind: "arrayRows"; array: string; attr?: string }
  /** A function's result, run when the step is sent; `value` (or a prompt's value) goes in place of val. */
  | { kind: "function"; call: string; value?: string; prompt?: string };

export interface Field {
  name: string;
  value: ValueSource;
}

export type VerifyMethod =
  | { method: "asyncActionGroup" }
  | { method: "paymentScheduleFlag"; flag: string }
  | { method: "pfiWorkunit" }
  | { method: "manual" }
  | { method: "newRequest"; cls: string; action: string; explicit?: boolean }
  | { method: "replicationSet"; set: string }
  | { method: "compassQuery" }
  | { method: "radarJob" }
  | { method: "custom"; function: string };

export type Submit = "async" | "direct" | "form" | "query";

export interface Step {
  id: string;
  seq: number;
  name: string;
  kind: "job" | "report" | "manual" | "none" | "compass" | "radar";
  infor?: { class: string; action: string; submit: Submit; fields: Field[] };
  /** Compass SQL steps (design §15.9.2): a SQL library entry, its pinned version and each parameter's value. */
  compass?: { sql: string; version: number; params: Record<string, ValueSource> };
  /** RADAR steps (design §15.5): a report (or the raw CSV) from an earlier Compass SQL step's query. */
  radar?: { op: "timerecord" | "register" | "compassExport"; queryFrom: string; pushToInfor?: boolean; reportName?: string; distProfile?: string };
  when?: { prompt: string; equals: string };
  pre?: string[];
  /** Functions run in the sandbox before the step is sent (legacy prefunction): call strings, run in order. */
  preFunctions?: string[];
  /** Functions run once the step is done (legacy postfunction). */
  postFunctions?: string[];
  /** Multiple runs: one job per row of an array (optionally only rows whose attribute equals a value or a prompt's value). */
  repeat?: { array: string; where?: { attr: string; equals: string } };
  /** A dynamic loop: up to max passes; a function before sending ends it with skipJob(). */
  loop?: { max: number };
  verify: VerifyMethod;
  pollSeconds: number;
  maxChecks: number;
  breakAfter: boolean;
  reports: { name: string; template: string }[];
  archives: { report: string; folder: string; file: string }[];
  ownReport?: string;
  rerun: boolean;
  instructions?: string;
  rerunDefault?: "auto" | "yes" | "no";
  verifiedBy?: string;
  /** Step branches this step starts when it completes (its reports are branches too, kept on the step here). */
  branches?: string[];
  /** All must be done or skipped before this step sends: a report branch (<step>_r<n>), a step branch or a step. */
  waitFor?: WaitRef[];
  /** A break pauses this step's own path (default) or the whole run. */
  breakScope?: "branch" | "run";
  /** A report step: its break waits for its own report's file. */
  waitsForOwnReport?: boolean;
}

export type WaitRef = { branch: string } | { step: string };

/** A step branch: a lane of steps that runs alongside, started when its step completes. */
export interface StepBranch {
  id: string;
  name: string;
  kind: "steps";
  /** A critical branch's failure holds the whole run; a non-critical one pauses only itself. */
  critical: boolean;
  steps: Step[];
}

export interface Prompt {
  code: string;
  label: string;
  legacyName?: string;
  legacyCode?: string;
  kind: "text" | "date" | "select";
  required: boolean;
  default?: string;
  options?: { value: string; label: string }[];
  lookup?: { builtin: string; fills?: { prompt: string; from: string }[] };
  setBy?: string;
  maxLength?: number;
}

export interface Definition {
  schema: string;
  id: string;
  type: string;
  name: string;
  application: string;
  product: "HCM" | "FSM";
  version: number;
  runKey: string[];
  runDatePrompt: string;
  prompts: Prompt[];
  templates: { id: string; data: string; source: string }[];
  trunk: Step[];
  builtins: string[];
  /** The automation's own functions (phase 5): key "name(args)" and JavaScript, run in the sandbox. */
  functions?: AutomationFunction[];
  /** Data tables (legacy ARRAY) for multiple-runs steps and arrayRows fields. */
  arrays?: DataArray[];
  /** The tree's step branches (report branches travel on their steps in this view). */
  branches?: StepBranch[];
}

export interface DataArray {
  name: string;
  desc?: string;
  attrs: string[];
  rows: { desc?: string; value: string; attrs: Record<string, string> }[];
}

export interface AutomationFunction {
  key: string;
  code: string;
  desc?: string;
  /** The function file's text, when the importer rewrote it. */
  source?: string;
}

/** A function name from its key or a call string: "buildTitle(val)" → "buildTitle". */
export const fnName = (keyOrCall: string) => keyOrCall.trim().replace(/\(.*$/, "");

export interface Issue {
  level: "error" | "warn";
  where: string;
  message: string;
}

export interface ActionParameter {
  name: string;
  type?: string;
  label?: string;
  states?: { name: string; value: string }[];
  required?: "always" | "conditional";
  default?: string;
}

/** Waypoint's reading of an action (the action catalog). */
export interface ActionKind {
  cls: string;
  action: string;
  kind: "plain" | "background" | "fanout" | "unknown";
  declared?: string;
  finishing?: { cls?: string; action: string };
  background?: { action: string; via?: string }[];
  parameters?: ActionParameter[];
  keys?: string[];
  rerun?: { field: string; flag?: string; required?: boolean };
  restricted?: boolean;
  source?: string;
  cu?: string;
  note?: string;
}

export const RUN_FLAGS = ["OvertimeRunFlag", "LockPayroll", "CalculatePayments", "PrintPayments", "ClosePayroll", "PostPayroll", "OffCycleClosePayroll", "TipsRunFlag"];

/** What the Designer suggests for an action (docs/phase3-plan.md §2; Chad 2026-10-10: background actions as background requests). */
export function suggestSubmit(k: ActionKind | undefined): Submit {
  // A Set action can't go through the action PUT ("Set actions not supported by executeImport", 2026-10-10).
  return k?.kind === "background" || k?.kind === "fanout" || k?.declared === "Set" ? "async" : "direct";
}

export function suggestCheck(cls: string, action: string, submit: Submit, k: ActionKind | undefined): VerifyMethod {
  if (cls === "ReplicationSet" && action === "Replicate") return { method: "replicationSet", set: "" };
  if (submit === "async") return { method: "asyncActionGroup" };
  if (cls === "PfiTrigger" && action === "Start") return { method: "pfiWorkunit" };
  // PaymentSchedule's run flags share the actions' names (LockPayroll, CalculatePayments…); BankCode.PrintPayments sets PrintPayments.
  const flag = k?.rerun?.flag ?? (RUN_FLAGS.includes(action) && (cls === "PaymentSchedule" || cls === "BankCode") ? action : undefined);
  if (flag) return { method: "paymentScheduleFlag", flag };
  if (k?.kind === "plain") return { method: "manual" };
  if (k?.kind === "fanout" && k.finishing) return { method: "newRequest", cls: k.finishing.cls ?? "", action: k.finishing.action };
  if (k?.kind === "background") return { method: "newRequest", cls, action };
  return { method: "manual" };
}

export const describeCheck = (v: VerifyMethod) =>
  v.method === "asyncActionGroup"
    ? "Its request's group completes"
    : v.method === "paymentScheduleFlag"
      ? `PaymentSchedule.${v.flag} settles`
      : v.method === "pfiWorkunit"
        ? "Its IPA work unit completes"
        : v.method === "newRequest"
          ? `New ${v.cls ? `${v.cls}.` : ""}${v.action} request completes`
          : v.method === "manual"
            ? "Done when Infor accepts it"
            : v.method === "replicationSet"
              ? `Replication set ${v.set || "?"} finishes (LastRefreshStamp moves, CurrentRefreshStamp clears)`
              : v.method === "compassQuery"
                ? "Compass reports the query FINISHED"
                : v.method === "radarJob"
                  ? "Its RADAR job completes"
                  : v.method === "custom"
                    ? `${v.function || "A function"} says the step is done`
                    : `Custom: ${(v as { function?: string }).function ?? ""}`;

export const newStepId = (trunk: Step[]) => {
  for (let n = trunk.length + 1; ; n++) if (!trunk.some((s) => s.id === `STEP_${n}`)) return `STEP_${n}`;
};

// ---- prompts: run-key slots and renames

/** Run-key slots (legacy objname): the run key is ORG-UNIT-1..4 then AUTO-DATE, by position (engine runKeyOf). */
export const SLOTS = [
  { value: "ORG-UNIT-1", label: "Org unit 1" },
  { value: "ORG-UNIT-2", label: "Org unit 2" },
  { value: "ORG-UNIT-3", label: "Org unit 3" },
  { value: "ORG-UNIT-4", label: "Org unit 4" },
  { value: "AUTO-DATE", label: "Run date (names the jobs)" },
];
export const slotOf = (p: Prompt) => SLOTS.find((x) => x.value === p.legacyName)?.value ?? "";

/** The run key and run date prompt, from the slots. */
export function withSlots(def: Definition, prompts: Prompt[]): Definition {
  const ou = prompts.filter((p) => /^ORG-UNIT-\d$/.test(p.legacyName ?? "")).sort((a, b) => a.legacyName!.localeCompare(b.legacyName!));
  const date = prompts.find((p) => p.legacyName === "AUTO-DATE");
  return { ...def, prompts, runKey: [...ou.map((p) => p.code), ...(date ? [date.code] : [])], runDatePrompt: date?.code ?? "" };
}

/** Puts a prompt in a slot (taking it from any other prompt); the run date must be a date. */
export function setSlot(def: Definition, code: string, slot: string): Definition {
  const prompts = def.prompts.map((p) => {
    if (p.code === code) return { ...p, legacyName: slot || undefined, ...(slot === "AUTO-DATE" ? { kind: "date" as const, required: true } : {}) };
    if (slot && p.legacyName === slot) return { ...p, legacyName: undefined };
    return p;
  });
  return withSlots(def, prompts);
}

const PLACEHOLDER = /\{\{prompt:([A-Za-z0-9_]+)(?:\|(yyyymmdd))?\}\}/g;
export const placeholders = (text: string) => [...new Set([...text.matchAll(PLACEHOLDER)].map((m) => m[1]))];
const renameIn = (text: string, from: string, to: string) => text.replace(PLACEHOLDER, (m, code: string, fmt?: string) => (code === from ? `{{prompt:${to}${fmt ? `|${fmt}` : ""}}}` : m));

/** Renames a prompt and every use of it: fields, conditions, templates, archive paths, lookups and the run key. */
export function renamePrompt(def: Definition, from: string, to: string): Definition {
  if (!to || from === to || def.prompts.some((p) => p.code === to)) return def;
  const value = (v: ValueSource): ValueSource => (v.kind === "prompt" && v.code === from ? { ...v, code: to } : v);
  return {
    ...def,
    runKey: def.runKey.map((k) => (k === from ? to : k)),
    runDatePrompt: def.runDatePrompt === from ? to : def.runDatePrompt,
    prompts: def.prompts.map((p) => ({ ...p, code: p.code === from ? to : p.code, ...(p.lookup?.fills ? { lookup: { ...p.lookup, fills: p.lookup.fills.map((f) => (f.prompt === from ? { ...f, prompt: to } : f)) } } : {}) })),
    templates: def.templates.map((t) => ({ ...t, data: renameIn(t.data, from, to) })),
    trunk: def.trunk.map((s) => ({
      ...s,
      ...(s.when?.prompt === from ? { when: { ...s.when, prompt: to } } : {}),
      ...(s.infor ? { infor: { ...s.infor, fields: s.infor.fields.map((f) => ({ ...f, value: value(f.value) })) } } : {}),
      archives: s.archives.map((a) => ({ ...a, folder: renameIn(a.folder, from, to), file: renameIn(a.file, from, to) })),
    })),
  };
}

/** Where a prompt is used (shown before it's removed). */
export function promptUses(def: Definition, code: string): string[] {
  const out: string[] = [];
  if (def.runDatePrompt === code) out.push("the run date");
  for (const p of def.prompts) if (p.lookup?.fills?.some((f) => f.prompt === code)) out.push(`${p.label}'s lookup`);
  for (const t of def.templates) if (placeholders(t.data).includes(code)) out.push(`template ${t.id}`);
  for (const s of def.trunk) {
    if (s.when?.prompt === code) out.push(`${s.name} (condition)`);
    if (s.infor?.fields.some((f) => f.value.kind === "prompt" && f.value.code === code)) out.push(`${s.name} (fields)`);
    if (s.archives.some((a) => placeholders(a.folder + a.file).includes(code))) out.push(`${s.name} (archive)`);
  }
  return out;
}

// ---- report templates

/** Renames a template and the reports and fields that use it. */
export function renameTemplate(def: Definition, from: string, to: string): Definition {
  if (!to || from === to || def.templates.some((t) => t.id === to)) return def;
  return {
    ...def,
    templates: def.templates.map((t) => (t.id === from ? { ...t, id: to } : t)),
    trunk: def.trunk.map((s) => ({
      ...s,
      reports: s.reports.map((r) => (r.template === from ? { ...r, template: to } : r)),
      ...(s.infor ? { infor: { ...s.infor, fields: s.infor.fields.map((f) => (f.value.kind === "report" && f.value.template === from ? { ...f, value: { kind: "report" as const, template: to } } : f)) } } : {}),
    })),
  };
}

export function templateUses(def: Definition, id: string): string[] {
  const out: string[] = [];
  for (const s of def.trunk) {
    for (const r of s.reports) if (r.template === id) out.push(`${s.name}: report ${r.name}`);
    if (s.infor?.fields.some((f) => f.value.kind === "report" && f.value.template === id)) out.push(`${s.name} (fields)`);
  }
  return out;
}

/** As the engine fills them (services/api/src/ja/templates.ts render). */
export function toYyyymmdd(v: string): string {
  const s = (v ?? "").trim();
  if (/^\d{8}$/.test(s)) return s;
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (m) return `${m[3]}${m[1].padStart(2, "0")}${m[2].padStart(2, "0")}`;
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (iso) return `${iso[1]}${iso[2]}${iso[3]}`;
  return s;
}
/** {{row}} / {{row:Attr}}: the array row of a step that runs for each row; previewed from `row` (or as [row]). */
const ROW = /\{\{row(?::([A-Za-z0-9_]+))?\}\}/g;
export const renderTemplate = (text: string, values: Record<string, string>, row?: { value: string; attrs: Record<string, string> }) =>
  text
    .replace(PLACEHOLDER, (_m, code: string, fmt?: string) => (fmt === "yyyymmdd" ? toYyyymmdd(values[code] ?? "") : (values[code] ?? "")))
    .replace(ROW, (_m, attr?: string) => (row ? (attr ? (row.attrs[attr] ?? "") : row.value) : `[row${attr ? `:${attr}` : ""}]`));

/** An archive path as the engine builds it: DOWNLOADS/<folder><file>, {{job}} the step's job name. */
export const renderArchive = (folder: string, file: string, values: Record<string, string>, job: string) => `DOWNLOADS/${renderTemplate(folder + file, values).replace(/\{\{job\}\}/g, job)}`;

/** Sample values for previews: each prompt's default, else a plausible value for its kind. */
export function sampleValues(def: Definition): Record<string, string> {
  return Object.fromEntries(def.prompts.map((p) => [p.code, p.default ?? (p.kind === "date" ? "2025-09-14" : p.options?.[0]?.value ?? p.label.toUpperCase().replace(/\s+/g, "_"))]));
}

// ---- the tree (docs/phase4-plan.md §4)

export const stepBranches = (def: Definition) => (def.branches ?? []).filter((b) => b.kind === "steps");
export const branchById = (def: Definition, id: string) => stepBranches(def).find((b) => b.id === id);

/** Every step: the trunk's, then each step branch's. */
export const allSteps = (def: Definition) => [...def.trunk, ...stepBranches(def).flatMap((b) => b.steps)];
export const findStep = (def: Definition, id: string) => allSteps(def).find((s) => s.id === id);
/** The step branch a step is in (undefined on the trunk). */
export const branchOfStep = (def: Definition, id: string) => stepBranches(def).find((b) => b.steps.some((s) => s.id === id));

/** Replaces a step wherever it is. */
export function updateStep(def: Definition, step: Step): Definition {
  if (def.trunk.some((s) => s.id === step.id)) return { ...def, trunk: def.trunk.map((s) => (s.id === step.id ? step : s)) };
  return { ...def, branches: (def.branches ?? []).map((b) => (b.steps.some((s) => s.id === step.id) ? { ...b, steps: b.steps.map((s) => (s.id === step.id ? step : s)) } : b)) };
}

/** Report branch ids, as the tree numbers them: the step's own report first, then the others in order. */
export const reportBranchIds = (s: Step) => [...(s.ownReport ? [s.ownReport] : []), ...s.reports.map((r) => r.name)].map((name, i) => ({ id: `${s.id}_r${i + 1}`, name }));

export const newBranchId = (def: Definition) => {
  for (let n = stepBranches(def).length + 1; ; n++) if (!stepBranches(def).some((b) => b.id === `BRANCH_${n}`)) return `BRANCH_${n}`;
};
export const newTreeStepId = (def: Definition) => {
  const ids = new Set(allSteps(def).map((s) => s.id));
  for (let n = ids.size + 1; ; n++) if (!ids.has(`STEP_${n}`)) return `STEP_${n}`;
};

/** The trunk position a step hangs off: its own on the trunk; its starting trunk step's in a branch. */
export function anchorOf(def: Definition, id: string): number {
  const t = def.trunk.findIndex((s) => s.id === id);
  if (t >= 0) return t;
  const b = branchOfStep(def, id);
  if (!b) return -1;
  const starter = allSteps(def).find((s) => s.branches?.includes(b.id));
  return starter ? anchorOf(def, starter.id) : -1;
}

/**
 * What a step may wait for (design §8.3): something started earlier, on the trunk or a branch hanging off an earlier
 * trunk step; never its own branches or itself.
 */
export function waitTargets(def: Definition, id: string): { ref: WaitRef; label: string }[] {
  const at = anchorOf(def, id);
  const me = findStep(def, id);
  const out: { ref: WaitRef; label: string }[] = [];
  for (const s of allSteps(def)) {
    if (s.id === id || anchorOf(def, s.id) >= at) continue;
    out.push({ ref: { step: s.id }, label: `Step ${s.name}` });
    for (const r of reportBranchIds(s)) out.push({ ref: { branch: r.id }, label: `Report ${r.name} (${s.name})` });
    for (const b of s.branches ?? []) {
      const br = branchById(def, b);
      if (br && !me?.branches?.includes(b)) out.push({ ref: { branch: b }, label: `Branch ${br.name}` });
    }
  }
  return out;
}
export const sameWait = (a: WaitRef, b: WaitRef) => ("step" in a && "step" in b && a.step === b.step) || ("branch" in a && "branch" in b && a.branch === b.branch);

/** A step's waits, named for people. */
export function waitLabel(def: Definition, w: WaitRef) {
  if ("step" in w) return findStep(def, w.step)?.name ?? w.step;
  const b = branchById(def, w.branch);
  if (b) return `branch ${b.name}`;
  for (const s of allSteps(def)) {
    const r = reportBranchIds(s).find((x) => x.id === w.branch);
    if (r) return `report ${r.name}`;
  }
  return w.branch;
}
